import { PATHS_COTIZADORES_V2, PATHS_TOFU_V2, STATUS_CATALOG } from "@helpers/constantes";

/**
 * Configuración del rail del sidebar.
 *
 * Cada fase enumera los status de `STATUS_CATALOG` que le pertenecen. El sidebar toma
 * `register.loan_status` y busca en qué fase cae; el orden entre fases lo da el propio arreglo,
 * así que no depende del `sequence` que devuelva el backend.
 *
 * Las pantallas que corren antes de que exista solicitud (cotizador, crear cuenta) no tienen
 * status: esas fases se reconocen por la ruta (`paths`). Ninguna pantalla declara nada: el rail es
 * función de la ruta y del avance real de la solicitud.
 */
export interface RailPhase {
  id: string;
  label: string;
  order: number;
  /** Status codes de `STATUS_CATALOG` que caen en esta fase. Vacío si el tramo corre sin solicitud. */
  codes: string[];
  /** Rutas de las pantallas previas a la solicitud que caen en esta fase (no tienen status). */
  paths?: string[];
}

export interface RailGroup {
  key: string;
  num: number;
  label: string;
  phases: RailPhase[];
}

/**
 * Fases por régimen. Hoy solo está definido PM; PFAE recorre la misma secuencia, por eso los
 * status propios de PFAE (`domicilio_pfae`, etc.) están incluidos en las fases que les tocan.
 * Expediente y Firma quedan sin fases hasta que existan esos flujos.
 */
export const configuraciones: Record<string, RailGroup[]> = {
  PM: [
    {
      key: "onboarding",
      num: 1,
      label: "Onboarding",
      phases: [
        {
          // El cotizador corre antes de que exista solicitud, así que no tiene status en el backend:
          // se reconoce por la ruta. También es la fase a la que cae el rail cuando no se resuelve.
          id: "cotizacion",
          label: "Cotización",
          order: 1,
          codes: [],
          paths: [PATHS_COTIZADORES_V2.credito_simple.path, PATHS_TOFU_V2.cotizador.path],
        },
        {
          id: "cuenta",
          label: "Cuenta",
          order: 2,
          codes: [STATUS_CATALOG.registro_datos_personales.code, STATUS_CATALOG.validacion_telefono.code],
          // Crear cuenta tampoco tiene status: la solicitud se crea al terminar esa pantalla.
          paths: [
            PATHS_TOFU_V2.crearCuenta.path,
            PATHS_TOFU_V2.registroDatosPersonales.path,
            PATHS_TOFU_V2.validacionTelefono.path,
          ],
        },
        {
          id: "empresa",
          label: "Empresa",
          order: 3,
          codes: [
            STATUS_CATALOG.datos_empresa_pm.code,
            STATUS_CATALOG.datos_empresa_pfae.code,
            STATUS_CATALOG.domicilio_pfae.code,
            STATUS_CATALOG.consulta_ciec.code,
            // Rama documental: cuando el usuario no comparte la CIEC.
            STATUS_CATALOG.desicion_sat.code,
            STATUS_CATALOG.documentos_sat.code,
            STATUS_CATALOG.consulta_buro_pm.code,
            STATUS_CATALOG.uso_de_credito.code,
            STATUS_CATALOG.informacion_garantia.code,
            STATUS_CATALOG.presencia_digital.code,
          ],
          // Flux: empresas con las que se usará la línea (sin status todavía).
          paths: [PATHS_TOFU_V2.informacionEmpresa.path, PATHS_TOFU_V2.usoLinea.path],
        },
        {
          id: "accionista",
          label: "Accionista",
          order: 4,
          codes: [
            STATUS_CATALOG.datos_accionista.code,
            STATUS_CATALOG.rfc_curp.code,
            STATUS_CATALOG.domicilio_accionista.code,
            STATUS_CATALOG.consulta_buro.code,
            STATUS_CATALOG.buro_bloqueado.code,
          ],
        },
        {
          id: "decision",
          label: "Decisión",
          order: 5,
          codes: [
            STATUS_CATALOG.waiting_room.code,
            STATUS_CATALOG.esperando_oferta.code,
            STATUS_CATALOG.waiting_room_revision.code,
            STATUS_CATALOG.oferta.code,
          ],
        },
      ],
    },
    { key: "expediente", num: 2, label: "Expediente", phases: [] },
    { key: "firma", num: 3, label: "Firma", phases: [] },
  ],
};

/**
 * Resuelve qué fase del rail está activa. Primero por la ruta actual (pantallas previas a la
 * solicitud, que no tienen status) y después por el avance real de la solicitud
 * (`register.loan_status`). La ruta va primero porque en esas pantallas un `loan_status` que quedó
 * en el store de una sesión anterior no describe dónde está el usuario.
 *
 * Devuelve el índice del grupo macro y de la fase; `-1` cuando no se pudo resolver.
 */
export const resolveRailPosition = (
  groups: RailGroup[],
  statusCode: string,
  pathname: string,
): { groupIndex: number; phaseIndex: number } => {
  for (let groupIndex = 0; groupIndex < groups.length; groupIndex += 1) {
    const phaseIndex = groups[groupIndex].phases.findIndex((phase) => phase.paths?.includes(pathname));
    if (phaseIndex !== -1) return { groupIndex, phaseIndex };
  }

  if (!statusCode) return { groupIndex: -1, phaseIndex: -1 };

  for (let groupIndex = 0; groupIndex < groups.length; groupIndex += 1) {
    const phaseIndex = groups[groupIndex].phases.findIndex((phase) => phase.codes.includes(statusCode));
    if (phaseIndex !== -1) return { groupIndex, phaseIndex };
  }

  return { groupIndex: -1, phaseIndex: -1 };
};

/**
 * Primer status de cada etapa del rail (por `key` del grupo), según el orden del `status_config`.
 * Una etapa va desde su inicio hasta el status anterior al inicio de la siguiente. Aplica igual para
 * PM, PFAE y PF. La primera etapa (Onboarding) no necesita inicio: es todo lo anterior a Expediente,
 * incluidas la sala de espera (CSCOF) y la oferta.
 */
export const RAIL_STAGE_STARTS: Partial<Record<string, string>> = {
  expediente: STATUS_CATALOG.check_list.code, // CSBEX
  firma: STATUS_CATALOG.bienvenida_firma.code, // CSBAF
};

/**
 * Último status de cada etapa. Solo decide cuando la etapa siguiente no tiene inicio propio en
 * `RAIL_STAGE_STARTS` (entonces empieza en el status que viene después); con inicio propio, los status
 * entre el fin de una etapa y el inicio de la otra se quedan en la anterior. El fin de la última
 * etapa queda documentado: lo que venga después sigue en Firma.
 */
export const RAIL_STAGE_ENDS: Partial<Record<string, string>> = {
  expediente: STATUS_CATALOG.validando_expediente_pm.code, // CSEDC (mismo código en PM, PFAE y PF)
  firma: STATUS_CATALOG.validacion_firma.code, // CSVDF
};

/**
 * Etapa activa del rail (índice del grupo):
 * 1. Pantallas previas a la solicitud (sin status): la etapa a la que pertenece su ruta.
 * 2. Con status: la última etapa cuyo inicio ya se alcanzó en `statusOrder` (el `status_config` del
 *    backend). El inicio es su código en `RAIL_STAGE_STARTS` o, si no tiene, el status que sigue al
 *    fin de la etapa anterior (`RAIL_STAGE_ENDS`).
 * 3. Si el status no viene en `statusOrder`: la resolución por fases (`resolveRailPosition`).
 * Si nada aplica, la primera etapa.
 */
export const resolveStageIndex = (
  groups: RailGroup[],
  statusOrder: string[],
  statusCode: string,
  pathname: string,
): number => {
  const byPath = groups.findIndex((group) => group.phases.some((phase) => phase.paths?.includes(pathname)));
  if (byPath !== -1) return byPath;
  if (!statusCode) return 0;

  const statusIndex = statusOrder.indexOf(statusCode);
  if (statusIndex !== -1) {
    for (let groupIndex = groups.length - 1; groupIndex > 0; groupIndex -= 1) {
      const start = RAIL_STAGE_STARTS[groups[groupIndex].key];
      const previousEnd = RAIL_STAGE_ENDS[groups[groupIndex - 1].key];
      let startIndex = -1;
      if (start) {
        startIndex = statusOrder.indexOf(start);
      } else if (previousEnd) {
        const endIndex = statusOrder.indexOf(previousEnd);
        startIndex = endIndex === -1 ? -1 : endIndex + 1;
      }
      if (startIndex !== -1 && statusIndex >= startIndex) return groupIndex;
    }
    return 0;
  }

  const { groupIndex } = resolveRailPosition(groups, statusCode, pathname);
  return groupIndex === -1 ? 0 : groupIndex;
};
